import { __awaiter, __extends, __generator } from "tslib";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { ElementChangedFlags, NodeUserData, PbrMaterial, SceneElement } from "../core/mdl";
import { deserializeNode, serializeNode } from "../core/mdl/io";
import { getRotationQuaternion, quaternionToAxisAngle } from "../core/utils";
import * as basket from "../eaiws/basket";
import { CalculationSheetInfo } from "../eaiws/basket";
import { ListenableEvent, Log } from "../utils";
import { ajax, Lock } from "../utils/async";
import { hash } from "../utils/arraybuffer";
import { contains } from "../utils/array";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError, rejectError } from "../utils/error";
import { getFileName, isNullOrEmpty } from "../utils/string";
import { defaultGetArticleDataOptions } from "./Defaults";
import { AppModule, ArticleElement, BoundingBoxDimensions, Helpers, SubArticleElement, SubArticleHighlights } from "./main";
var MainArticleElement = /** @class */ (function (_super) {
    __extends(MainArticleElement, _super);
    function MainArticleElement(pBasketId, pManager, pNode) {
        var _this = _super.call(this, pBasketId, pManager, pNode) || this;
        /**
         * Will be triggered inside updateGeometry() if the geometry was successfully updated.
         */
        _this.eventGeometryChanged = new ListenableEvent(_this);
        _this.mArticleDataCache = new Map();
        _this.mArticleDataOptionsCache = new Map();
        _this.mBasketItemCache = new Map();
        _this.mItemPropertiesCache = new Map();
        _this.mCalculationCache = new Map();
        _this.mCatalogImageIdCache = new Map();
        _this.mOapInteractorsCache = new Map();
        _this.mOapInteractorsCacheLock = new Lock();
        _this.mSubArticleCache = null;
        _this.mHasSubArticles = false;
        _this.mHasSelectableSubArticles = false;
        _this.mLastKnownUpdateState = "Unknown";
        _this.mUpdateGeometryDepth = 0;
        _this.mPendingGeometryUpdate = false;
        _this.mWaitGeometryUpdatePromise = null;
        _this.mWaitGeometryUpdateResolve = null;
        _this.mObxUrl = null;
        _this.mEnsureObxPromise = null;
        _this.mEnsureIsInstancedPromise = null;
        _this.mHideMainArticle = false;
        _this.mVisibleSubArticles = null;
        _this.mSubArticleHighlights = null;
        _this.mEaiwsTransformUpToDate = false;
        _this.mHasPlaceholderGeometry = false;
        _this.mBoxDimensions = null;
        _this.mDimensionsScaleFactor = 1.0;
        _this.mDimensionsColor = "#595959";
        _this.mModel.beginModification();
        _this.createOrUpdateSubArticleElements();
        _this.mModel.endModification();
        if (_this.isInstanced())
            _this.mMngr._registerInstancedArticle(_this);
        return _this;
    }
    Object.defineProperty(MainArticleElement.prototype, "hasPlaceholderGeometry", {
        get: function () {
            return (this.mHasPlaceholderGeometry);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MainArticleElement.prototype, "hideMainArticle", {
        /**
         * If true the main article will not be visible. Only the sub-articles will be visible.
         */
        get: function () {
            return (this.mHideMainArticle);
        },
        set: function (pHide) {
            if (pHide !== this.mHideMainArticle) {
                this.mHideMainArticle = pHide;
                this.updateMainArticleVisibility();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MainArticleElement.prototype, "visibleSubArticles", {
        /**
         * Array of sub-article ids. If not null only the sub-articles contained in this array are visible
         * otherwise all sub-articles will be visible.
         */
        get: function () {
            return (this.mVisibleSubArticles);
        },
        set: function (pSubArticles) {
            if (pSubArticles !== this.mVisibleSubArticles) {
                this.mVisibleSubArticles = pSubArticles;
                this.updateSubArticleVisibility();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MainArticleElement.prototype, "showDimensions", {
        get: function () {
            return (this.mBoxDimensions != null);
        },
        set: function (pShow) {
            if (pShow && this.mBoxDimensions == null) {
                this.mBoxDimensions = new BoundingBoxDimensions([this], this.mMngr, {
                    color: this.dimensionsColor,
                    scaleFactor: this.dimensionsScaleFactor
                });
            }
            else if (!pShow && this.mBoxDimensions != null) {
                this.mBoxDimensions.dispose();
                this.mBoxDimensions = null;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MainArticleElement.prototype, "dimensionsScaleFactor", {
        /**
         * Custom scale factor of the dimension.
         * @defaultValue 1
         */
        get: function () {
            return (this.mDimensionsScaleFactor);
        },
        set: function (pFactor) {
            if (pFactor !== this.mDimensionsScaleFactor) {
                this.mDimensionsScaleFactor = pFactor;
                this.updateBoxDimensions();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MainArticleElement.prototype, "dimensionsColor", {
        /**
         * Color of the dimensions as hex string.
         * @defaultValue "#595959"
         */
        get: function () {
            return (this.mDimensionsColor);
        },
        set: function (pColor) {
            if (pColor !== this.mDimensionsColor) {
                this.mDimensionsColor = pColor;
                this.updateBoxDimensions();
            }
        },
        enumerable: false,
        configurable: true
    });
    MainArticleElement.prototype.canSelectSubElement = function () {
        return (true);
    };
    MainArticleElement.prototype.dispose = function () {
        //dispose dimensions
        this.showDimensions = false;
        if (this.mSubArticleHighlights != null)
            this.mSubArticleHighlights.dispose();
        //dispose subarticles
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach(function (pSubElement) {
                pSubElement.dispose();
            });
        }
        _super.prototype.dispose.call(this);
    };
    MainArticleElement.prototype.isInstanced = function () {
        return (!isNullOrEmpty(this.mBasketId));
    };
    MainArticleElement.prototype.ensureIsInstanced = function () {
        var _this = this;
        if (this.mEnsureIsInstancedPromise != null)
            return (this.mEnsureIsInstancedPromise); //reentrant, return existing promise
        if (this.isInstanced())
            return (Promise.resolve());
        this.mEnsureIsInstancedPromise = this.ensureObx().then(function () {
            var tOptions = new basket.PasteOptions();
            tOptions.reuseItemIds = true;
            tOptions.returnOriginalItemIds = true;
            return (_this.mMngr.session.basket.paste(_this.mMngr.fatherItemId, null, _this.mObxUrl, tOptions));
        }).then(function (pIds) {
            _this.recoverInstance(pIds);
            _this.mEnsureIsInstancedPromise = null;
            _this.mMngr.eventArticleInstanceRestored.trigger(_this);
        }).catch(function (pError) {
            Log.error("Failed to instantiate article", pError);
            _this.mEnsureIsInstancedPromise = null;
        });
        return (this.mEnsureIsInstancedPromise);
    };
    MainArticleElement.prototype.getMainArticle = function () {
        return (this);
    };
    MainArticleElement.prototype.getParentArticle = function () {
        return (null);
    };
    MainArticleElement.prototype.hasSubArticles = function (pSelectableOnly) {
        return (pSelectableOnly ? this.mHasSelectableSubArticles : this.mHasSubArticles);
    };
    /**
     * Retrieves the basket items which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    MainArticleElement.prototype.fetchBasketItems = function () {
        var _this = this;
        var tPromise = this.ensureIsInstanced().then(function () {
            if (_this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            var tItemIds = new Array();
            //main article
            if (_this.getCachedBasketItem() == null)
                tItemIds.push(_this.mBasketId);
            //sub articles
            var tSubArticles = _this.getSubArticles(false);
            for (var _i = 0, tSubArticles_1 = tSubArticles; _i < tSubArticles_1.length; _i++) {
                var tSubArtile = tSubArticles_1[_i];
                if (tSubArtile.getCachedBasketItem() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            var tPromise = _this.getEaiwsBasketItems(tItemIds).then(function (pItems) {
                if (pItems != null) {
                    for (var _i = 0, pItems_1 = pItems; _i < pItems_1.length; _i++) {
                        var tItem = pItems_1[_i];
                        _this.mBasketItemCache.set(tItem.itemId, tItem);
                    }
                }
            });
            return (tPromise);
        });
        return (tPromise);
    };
    /**
     * Fills the internal cache with the given items.
     */
    MainArticleElement.prototype.fillBasketItemCache = function (pItems) {
        for (var _i = 0, pItems_2 = pItems; _i < pItems_2.length; _i++) {
            var tItem = pItems_2[_i];
            this.mBasketItemCache.set(tItem.itemId, tItem);
        }
    };
    /**
     * Retrieves the item properties which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    MainArticleElement.prototype.fetchItemProperties = function () {
        var _this = this;
        var tPromise = this.ensureIsInstanced().then(function () {
            if (_this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            var tItemIds = new Array();
            //main article
            if (_this.getCachedItemProperties() == null)
                tItemIds.push(_this.mBasketId);
            //sub articles
            var tSubArticles = _this.getSubArticles(false);
            for (var _i = 0, tSubArticles_2 = tSubArticles; _i < tSubArticles_2.length; _i++) {
                var tSubArtile = tSubArticles_2[_i];
                if (tSubArtile.getCachedItemProperties() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            var tPromise = _this.getEaiwsItemProps(tItemIds).then(function (pItemProperties) {
                if (pItemProperties != null) {
                    for (var _i = 0, pItemProperties_1 = pItemProperties; _i < pItemProperties_1.length; _i++) {
                        var tProps = pItemProperties_1[_i];
                        _this.mItemPropertiesCache.set(tProps.itemId, tProps);
                    }
                }
            });
            return (tPromise);
        });
        return (tPromise);
    };
    /**
     * Fills the internal cache with the given item properties.
     */
    MainArticleElement.prototype.fillItemPropertiesCache = function (pItemProps) {
        for (var _i = 0, pItemProps_1 = pItemProps; _i < pItemProps_1.length; _i++) {
            var tProps = pItemProps_1[_i];
            if (tProps.itemId != null) {
                this.mItemPropertiesCache.set(tProps.itemId, tProps);
            }
        }
    };
    /**
     * Retrieves the calculations which are not already cached (MainArticle + SubArticles)
     * from the EAIWS and adds them to the cache.
     */
    MainArticleElement.prototype.fetchCalculations = function () {
        var _this = this;
        var tPromise = this.ensureIsInstanced().then(function () {
            if (_this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            var tProc = _this.mMngr.pricingProcedure;
            if (tProc == null)
                return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
            var tItemIds = new Array();
            //main article
            if (_this.getCachedCalculation() == null)
                tItemIds.push(_this.mBasketId);
            //sub articles
            var tSubArticles = _this.getSubArticles(false);
            for (var _i = 0, tSubArticles_3 = tSubArticles; _i < tSubArticles_3.length; _i++) {
                var tSubArtile = tSubArticles_3[_i];
                if (tSubArtile.getCachedCalculation() == null && tSubArtile.basketId != null)
                    tItemIds.push(tSubArtile.basketId);
            }
            if (tItemIds.length === 0)
                return (Promise.resolve());
            var tPromise = _this.getEaiwsItemCalculation(tItemIds).then(function (pItemCalcs) {
                for (var _i = 0, pItemCalcs_1 = pItemCalcs; _i < pItemCalcs_1.length; _i++) {
                    var tItemCalc = pItemCalcs_1[_i];
                    var tCalcInfo = new CalculationSheetInfo(tProc, tItemCalc.sheet);
                    _this.mCalculationCache.set(tItemCalc.id, tCalcInfo);
                }
            });
            return (tPromise);
        });
        return (tPromise);
    };
    /**
     * Fills the internal cache with the given calculations.
     */
    MainArticleElement.prototype.fillCalculationCache = function (pCalculations) {
        var _this = this;
        pCalculations.forEach(function (pValue, pKey) {
            _this.mCalculationCache.set(pKey, pValue);
        });
    };
    /**
     * Fills the internal cache with the given data.
     */
    MainArticleElement.prototype.fillConfigDataCache = function (pData) {
        var _this = this;
        pData.forEach(function (pValue, pKey) {
            if (pValue.articleData != null)
                _this.mArticleDataCache.set(pKey, pValue.articleData);
            if (pValue.articleDataOptions != null)
                _this.mArticleDataOptionsCache.set(pKey, pValue.articleDataOptions);
            if (pValue.oapInteractors != null)
                _this.mOapInteractorsCache.set(pKey, pValue.oapInteractors);
        });
    };
    /**
     * Returns a calculation for the main article including all its sub articles.
     */
    MainArticleElement.prototype.getCompositeCalculation = function () {
        return __awaiter(this, void 0, void 0, function () {
            var tProc, tSheet, tFallbackCalc;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.ensureIsInstanced()];
                    case 1:
                        _a.sent();
                        if (this.mBasketId == null)
                            throw newError(ERR_INVALID_STATE, "basketId is null");
                        tProc = this.mMngr.pricingProcedure;
                        if (tProc == null)
                            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
                        if (!(this.mCompositeCalculation !== undefined)) return [3 /*break*/, 2];
                        return [2 /*return*/, (this.mCompositeCalculation)];
                    case 2: return [4 /*yield*/, this.getEaiwsCompositeCalculation(this.mBasketId)];
                    case 3:
                        tSheet = _a.sent();
                        if (!(tSheet.quantity == null)) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.getCalculation()];
                    case 4:
                        tFallbackCalc = _a.sent();
                        tSheet.quantity = tFallbackCalc.sheet.quantity;
                        _a.label = 5;
                    case 5:
                        this.mCompositeCalculation = new CalculationSheetInfo(tProc, tSheet);
                        return [2 /*return*/, (this.mCompositeCalculation)];
                }
            });
        });
    };
    /**
     * Notify this main article that there was a change.
     * @param pArticle The changed article. Can be a sub article of the main article or the main article itself.
     * @param pWholeComposite Shoud be true if the given article and also other articles have changed.
     */
    MainArticleElement.prototype.notifyArticleChanged = function (pArticle, pWholeComposite) {
        this._clearCachedArticleInfo(pArticle, pWholeComposite);
        this.mMngr._notifyArticleChanged(pWholeComposite ? this : pArticle, pWholeComposite);
    };
    MainArticleElement.prototype.updateGeometry = function (pValidateSelection) {
        if (pValidateSelection === void 0) { pValidateSelection = true; }
        return __awaiter(this, void 0, void 0, function () {
            var tNode, tItems, tResult, tSubArticleIds, _i, tItems_1, tItem;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.mPendingGeometryUpdate = true;
                        if (this.mUpdateGeometryDepth > 0)
                            return [2 /*return*/];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 7, 8]);
                        this.mModel.beginModification();
                        this.beginChange();
                        return [4 /*yield*/, this.ensureIsInstanced()];
                    case 2:
                        _a.sent();
                        if (this.mBasketId == null)
                            return [2 /*return*/, (rejectError(ERR_INVALID_STATE, "basketId is null"))];
                        tNode = null;
                        tItems = null;
                        return [4 /*yield*/, this.mMngr._importGfj(this.mBasketId, null)];
                    case 3:
                        tResult = _a.sent();
                        if (tResult != null) {
                            tNode = tResult.node;
                            tItems = tResult.items;
                            this._setGfjUrl(tResult.gfjUrl);
                            this.fillBasketItemCache(tResult.items);
                        }
                        if (!(tNode == null)) return [3 /*break*/, 5];
                        tSubArticleIds = new Array();
                        if (tItems != null) {
                            for (_i = 0, tItems_1 = tItems; _i < tItems_1.length; _i++) {
                                tItem = tItems_1[_i];
                                if (tItem.itemId != this.basketId)
                                    tSubArticleIds.push(tItem.itemId);
                            }
                        }
                        this.mHasPlaceholderGeometry = true;
                        return [4 /*yield*/, this.mMngr.placeholderCreator.create(this, tSubArticleIds)];
                    case 4:
                        tNode = _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        this.mHasPlaceholderGeometry = false;
                        _a.label = 6;
                    case 6:
                        this.replaceNode(tNode, true, true);
                        this.createOrUpdateSubArticleElements();
                        if (pValidateSelection) {
                            this.mMngr.app.model.validateSelection();
                        }
                        //apply visibility
                        if (this.mHideMainArticle)
                            this.updateMainArticleVisibility();
                        if (this.mVisibleSubArticles != null)
                            this.updateSubArticleVisibility();
                        this.eventGeometryChanged.trigger();
                        return [3 /*break*/, 8];
                    case 7:
                        this.mPendingGeometryUpdate = false;
                        this.endChange();
                        this.mModel.endModification();
                        //resolve the waiting promise
                        if (this.mWaitGeometryUpdatePromise != null) {
                            this.mWaitGeometryUpdateResolve();
                            this.mWaitGeometryUpdateResolve = null;
                            this.mWaitGeometryUpdatePromise = null;
                        }
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    MainArticleElement.prototype.beginUpdateGeometry = function () {
        this.mUpdateGeometryDepth++;
    };
    MainArticleElement.prototype.endUpdateGeometry = function () {
        this.mUpdateGeometryDepth--;
        if (this.mUpdateGeometryDepth <= 0) {
            this.mUpdateGeometryDepth = 0;
            if (this.mPendingGeometryUpdate)
                return (this.updateGeometry(true));
        }
        return (Promise.resolve());
    };
    /**
     * If there is a pending geometry update this function will wait until it is finished,
     * otherwise it will resovle immediately.
     */
    MainArticleElement.prototype.waitUntilGeometryUpdated = function () {
        var _this = this;
        if (this.mPendingGeometryUpdate) {
            if (this.mWaitGeometryUpdatePromise == null) {
                this.mWaitGeometryUpdatePromise = new Promise(function (pResolve) {
                    _this.mWaitGeometryUpdateResolve = pResolve;
                });
            }
            return (this.mWaitGeometryUpdatePromise);
        }
        else
            return (Promise.resolve());
    };
    MainArticleElement.prototype.clone = function (pNewParent) {
        var tClone = new MainArticleElement(null, this.mMngr, SceneElement.cloneNode(this.mNode, pNewParent));
        tClone.copyDataFrom(this);
        return (tClone);
    };
    MainArticleElement.prototype.serialize = function (pContext) {
        var _this = this;
        if (this.mNode == null)
            return (null);
        var tIOArticle = {};
        tIOArticle.classId = MainArticleElement.CLASS_ID;
        if (this.mHasPlaceholderGeometry)
            tIOArticle.placeholderGeometry = true;
        tIOArticle.node = serializeNode(this.mMngr.app.model, pContext, this.mNode, true, function (pNode, pIONode) {
            //add aticle information
            var tBasketId = NodeUserData.GetData(pNode, ArticleElement.NODEDATA_BASKETID);
            if (!isNullOrEmpty(tBasketId)) {
                var tArticleNode = pIONode;
                //basket id
                tArticleNode.basketId = tBasketId;
                //catalog image
                var tCatImage = _this._getCatalogImageId(tBasketId);
                if (tCatImage !== undefined) { //cache may contain null
                    var tCatImageIdx = AppModule._GetCatalogImageIndex(tCatImage, pContext);
                    if (tCatImageIdx != null)
                        tArticleNode.catalogImage = tCatImageIdx;
                    else
                        tArticleNode.catalogImage = null;
                }
                //selectable
                var tSelectable = NodeUserData.GetData(pNode, ArticleElement.NODEDATA_SELECTABLE);
                if (tSelectable != null && !tSelectable)
                    tArticleNode.selectable = false;
            }
        });
        return (tIOArticle);
    };
    /**
     * Returns the currently known ofml update state.
     */
    MainArticleElement.prototype.getKnownOfmlUpdateState = function () {
        var tProps = this.getCachedItemProperties();
        if (tProps != null && tProps.article != null && tProps.article.ofmlUpdateState != null) {
            //do not override known states, item props do not return states like "NoCatalog" or "Invalid"
            if (this.mLastKnownUpdateState === "Unknown" || tProps.article.ofmlUpdateState === "UpToDate") {
                this.mLastKnownUpdateState = tProps.article.ofmlUpdateState;
            }
        }
        return (this.mLastKnownUpdateState);
    };
    MainArticleElement.prototype.getOfmlUpdateState = function () {
        var _this = this;
        var tKnownState = this.getKnownOfmlUpdateState();
        if (tKnownState !== "Unknown") {
            return (Promise.resolve(tKnownState));
        }
        var tPromise = this.ensureIsInstanced().then(function () {
            if (_this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (ArticleElement._GetOfmlUpdateState(_this.mMngr.session, _this.mBasketId));
        }).then(function (pState) {
            //update cached state
            _this.mLastKnownUpdateState = pState;
            return (pState);
        });
        return (tPromise);
    };
    /**
     * Updates the ofml article and its sub articles to use the currently installed product data.
     * If an update is necessary can be checked with 'getOfmlUpdateState()'. Returns true if the
     * update was successful.
     * @pMigrate set to true if migration should be performed if necessary
     * @pUpdateGeometry set to true if geometry should also be updated
     * @pPriceDate price date to use for the update
     */
    MainArticleElement.prototype.updateOfmlArticle = function (pMigrate, pUpdateGeometry, pPriceDate) {
        return __awaiter(this, void 0, void 0, function () {
            var tNewState, tSubArticles, _i, tSubArticles_4, tSubArticle;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.ensureIsInstanced()];
                    case 1:
                        _a.sent();
                        if (this.mBasketId == null)
                            throw newError(ERR_INVALID_STATE, "basketId is null");
                        return [4 /*yield*/, MainArticleElement._UpdateOfmlArticle(this.mMngr.session, this.mBasketId, pMigrate, pPriceDate)];
                    case 2:
                        tNewState = _a.sent();
                        //update cached state
                        this.mLastKnownUpdateState = tNewState;
                        if (!(tNewState === "UpToDate")) return [3 /*break*/, 5];
                        this._clearCachedArticleInfo(this, true);
                        //trigger property change event
                        this.notifyChange(ElementChangedFlags.Properties);
                        tSubArticles = this.getSubArticles(false);
                        for (_i = 0, tSubArticles_4 = tSubArticles; _i < tSubArticles_4.length; _i++) {
                            tSubArticle = tSubArticles_4[_i];
                            tSubArticle.notifyChange(ElementChangedFlags.Properties);
                        }
                        if (!pUpdateGeometry) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.updateGeometry()];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4:
                        this.mMngr._notifyArticleChanged(this, true);
                        return [2 /*return*/, (true)];
                    case 5: return [2 /*return*/, (false)];
                }
            });
        });
    };
    /**
     * Enables highlighting of selectabe sub articles for this main article. The highlighting will be disabled
     * automatically e.g. if the article changed or a tool was started.
     * Returns false if the default tool is currently not running (if a default tool exists)
     * or the highlighting is enabled already.
     * @param pCreateCallback Called after creation of the highlighting.
     * @param pDisposeCallback Called if the highighting was diposed/disabled.
     */
    MainArticleElement.prototype.highlightSubArticles = function (pCreateCallback, pDisposeCallback) {
        var _this = this;
        if (this.mSubArticleHighlights != null)
            return (false);
        this.mSubArticleHighlights = SubArticleHighlights.Create(this, this.mMngr, pCreateCallback, function () {
            _this.mSubArticleHighlights = null;
            if (pDisposeCallback != null)
                pDisposeCallback();
        });
        return (this.mSubArticleHighlights != null);
    };
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    MainArticleElement.prototype._initPlaceholderGeometry = function (pItems) {
        return __awaiter(this, void 0, void 0, function () {
            var tSubArticleIds, _i, pItems_3, tItem, tChildNodes, _a, tChildNodes_1, tChild, tSubId, tMesh;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, , 2, 3]);
                        this.mModel.beginModification();
                        tSubArticleIds = new Array();
                        if (pItems != null) {
                            //use given ids
                            for (_i = 0, pItems_3 = pItems; _i < pItems_3.length; _i++) {
                                tItem = pItems_3[_i];
                                if (tItem.itemId != this.basketId)
                                    tSubArticleIds.push(tItem.itemId);
                            }
                        }
                        else {
                            //use current sub nodes
                            if (this.mNode != null) {
                                tChildNodes = this.mNode.getChildTransformNodes(false);
                                for (_a = 0, tChildNodes_1 = tChildNodes; _a < tChildNodes_1.length; _a++) {
                                    tChild = tChildNodes_1[_a];
                                    tSubId = NodeUserData.GetData(tChild, ArticleElement.NODEDATA_BASKETID);
                                    if (tSubId != null)
                                        tSubArticleIds.push(tSubId);
                                }
                            }
                        }
                        return [4 /*yield*/, this.mMngr.placeholderCreator.create(this, tSubArticleIds)];
                    case 1:
                        tMesh = _b.sent();
                        if (this.mNode == null)
                            this.setNode(tMesh);
                        else
                            this.replaceNode(tMesh, true, true);
                        this.mHasPlaceholderGeometry = true;
                        this.createOrUpdateSubArticleElements();
                        this.mModel.endModification();
                        return [3 /*break*/, 3];
                    case 2:
                        this.mModel.endModification();
                        return [7 /*endfinally*/];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /** @internal */
    MainArticleElement.prototype._clearCachedArticleInfo = function (pArticle, pWholeComposite) {
        if (pWholeComposite) {
            //clear whole cache
            this.mArticleDataCache.clear();
            this.mArticleDataOptionsCache.clear();
            this.mBasketItemCache.clear();
            this.mItemPropertiesCache.clear();
            this.mCalculationCache.clear();
            this.mCatalogImageIdCache.clear();
            this.mOapInteractorsCache.clear();
        }
        else {
            if (pArticle != null && pArticle.basketId != null) {
                this.mArticleDataCache.delete(pArticle.basketId);
                this.mArticleDataOptionsCache.delete(pArticle.basketId);
                this.mBasketItemCache.delete(pArticle.basketId);
                this.mItemPropertiesCache.delete(pArticle.basketId);
                this.mCalculationCache.delete(pArticle.basketId);
                this.mCatalogImageIdCache.delete(pArticle.basketId);
                this.mOapInteractorsCache.delete(pArticle.basketId);
            }
        }
        this.mCompositeCalculation = undefined;
        //reset cached obx
        this.mObxUrl = null;
        //article changed, disable highlights
        if (this.mSubArticleHighlights != null) {
            this.mSubArticleHighlights.dispose();
        }
    };
    /** @internal */
    MainArticleElement.prototype._getArticleData = function (pBasketId, pOptions) {
        var _this = this;
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        var tRequestedOptions = pOptions !== null && pOptions !== void 0 ? pOptions : defaultGetArticleDataOptions;
        tRequestedOptions.separateCurrencies = true; //force to be true
        //check cache first
        var tArticleData = this._getCachedArticleData(pBasketId, tRequestedOptions);
        if (tArticleData != null)
            return (Promise.resolve(tArticleData));
        var tPromise;
        if (this.mMngr.session.isValid) {
            tPromise = this.mMngr.session.basket.getArticleData(pBasketId, tRequestedOptions);
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        tPromise = tPromise.then(function (pArticleData) {
            var tCachedOptions = _this.mArticleDataOptionsCache.get(pBasketId);
            var tCachedData = _this.mArticleDataCache.get(pBasketId);
            if (tCachedOptions == null || tCachedData == null) {
                //nothing cached yet, add entry
                var tOptions = new basket.GetArticleDataOptions();
                Object.assign(tOptions, tRequestedOptions); //create copy
                _this.mArticleDataCache.set(pBasketId, pArticleData);
                _this.mArticleDataOptionsCache.set(pBasketId, tOptions);
            }
            else {
                //update cached articledata
                if (pArticleData != null) {
                    //properties
                    if (!tRequestedOptions.noProperties) {
                        tCachedOptions.noProperties = false;
                        tCachedData.properties = pArticleData.properties;
                        tCachedData.propertyClasses = pArticleData.propertyClasses;
                    }
                    //catalog icon
                    if (tRequestedOptions.fetchCatalogIcon) {
                        tCachedOptions.fetchCatalogIcon = true;
                        tCachedData.catalogIcon = pArticleData.catalogIcon;
                    }
                    //catalog image
                    if (tRequestedOptions.fetchCatalogImage) {
                        tCachedOptions.fetchCatalogImage = true;
                        tCachedData.catalogImage = pArticleData.catalogImage;
                    }
                }
            }
            return (pArticleData);
        });
        return (tPromise);
    };
    /** @internal */
    MainArticleElement.prototype._getCachedArticleData = function (pBasketId, pOptions) {
        var tRequestedOptions = pOptions;
        if (tRequestedOptions == null)
            tRequestedOptions = defaultGetArticleDataOptions;
        //compare cached with requested options
        var tCachedOptions = this.mArticleDataOptionsCache.get(pBasketId);
        if (tCachedOptions == null)
            return (undefined); //nothing cached
        //properties not cached
        if (tCachedOptions.noProperties && !tRequestedOptions.noProperties)
            return (undefined);
        //icon not cached
        if (!tCachedOptions.fetchCatalogIcon && tRequestedOptions.fetchCatalogIcon)
            return (undefined);
        //image not cached
        if (!tCachedOptions.fetchCatalogImage && tRequestedOptions.fetchCatalogImage)
            return (undefined);
        return (this.mArticleDataCache.get(pBasketId));
    };
    /** @internal */
    MainArticleElement.prototype._getBasketItem = function (pBasketId) {
        var _this = this;
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        var tItem = this.mBasketItemCache.get(pBasketId);
        if (tItem != null)
            return (Promise.resolve(tItem));
        var tPromise = this.getEaiwsBasketItems([pBasketId]).then(function (pItems) {
            if (pItems.length !== 0) {
                _this.mBasketItemCache.set(pBasketId, pItems[0]);
                return (Promise.resolve(pItems[0]));
            }
            else {
                return (Promise.reject(new Error("Failed to get item " + pBasketId)));
            }
        });
        return (tPromise);
    };
    /** @internal */
    MainArticleElement.prototype._getCachedBasketItem = function (pBasketId) {
        return (this.mBasketItemCache.get(pBasketId));
    };
    /** @internal */
    MainArticleElement.prototype._getItemProperties = function (pBasketId) {
        var _this = this;
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        var tItemProperties = this.mItemPropertiesCache.get(pBasketId);
        if (tItemProperties != null)
            return (Promise.resolve(tItemProperties));
        var tPromise = this.getEaiwsItemProps([pBasketId]).then(function (pItemProperties) {
            if (pItemProperties.length !== 0) {
                _this.mItemPropertiesCache.set(pBasketId, pItemProperties[0]);
                return (Promise.resolve(pItemProperties[0]));
            }
            else {
                return (Promise.reject(new Error("Failed to get item properties for item " + pBasketId)));
            }
        });
        return (tPromise);
    };
    /** @internal */
    MainArticleElement.prototype._getCachedItemProperties = function (pBasketId) {
        return (this.mItemPropertiesCache.get(pBasketId));
    };
    /** @internal */
    MainArticleElement.prototype._getCalculation = function (pBasketId) {
        var _this = this;
        if (pBasketId == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        //check cache first
        var tCalculation = this.mCalculationCache.get(pBasketId);
        if (tCalculation != null)
            return (Promise.resolve(tCalculation));
        var tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        var tPromise = this.getEaiwsItemCalculation([pBasketId]).then(function (pItemCalcs) {
            if (pItemCalcs.length !== 0) {
                var tCalcInfo = new CalculationSheetInfo(tProc, pItemCalcs[0].sheet);
                _this.mCalculationCache.set(pBasketId, tCalcInfo);
                return (Promise.resolve(tCalcInfo));
            }
            else {
                return (Promise.reject(new Error("Failed to get calculation for item " + pBasketId)));
            }
        });
        return (tPromise);
    };
    /** @internal */
    MainArticleElement.prototype._getCachedCalculation = function (pBasketId) {
        return (this.mCalculationCache.get(pBasketId));
    };
    /** @internal **/
    MainArticleElement.prototype._getCatalogImage = function (pBasketId) {
        return __awaiter(this, void 0, void 0, function () {
            var tImageId, tOptions, tArticleData, tCatalogImage, tBuffer, tImage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pBasketId == null)
                            return [2 /*return*/, (null)];
                        tImageId = this.mCatalogImageIdCache.get(pBasketId);
                        if (tImageId !== undefined) //cache may contain null
                            return [2 /*return*/, (Promise.resolve(this.mMngr._getCatalogImageFromCache(tImageId)))];
                        tOptions = new basket.GetArticleDataOptions();
                        tOptions.noProperties = true;
                        tOptions.fetchCatalogIcon = true;
                        tOptions.fetchCatalogImage = true;
                        return [4 /*yield*/, this._getArticleData(pBasketId, tOptions)];
                    case 1:
                        tArticleData = _a.sent();
                        tCatalogImage = undefined;
                        tCatalogImage = tArticleData.catalogImage;
                        if (isNullOrEmpty(tCatalogImage))
                            tCatalogImage = tArticleData.catalogIcon;
                        if (!isNullOrEmpty(tCatalogImage)) return [3 /*break*/, 2];
                        this.mCatalogImageIdCache.set(pBasketId, null);
                        return [2 /*return*/, (null)];
                    case 2: return [4 /*yield*/, ajax("GET", tCatalogImage, null, { responseType: "arraybuffer" })];
                    case 3:
                        tBuffer = _a.sent();
                        return [4 /*yield*/, hash(tBuffer, "SHA-256")];
                    case 4:
                        tImageId = _a.sent();
                        tImageId = tImageId.substr(0, 32); // reduce to 128 bit, should be enough in this case
                        tImage = {};
                        tImage.data = tBuffer;
                        tImage.name = getFileName(tCatalogImage);
                        this.mMngr._addCatalogImageToCache(tImageId, tImage);
                        this.mCatalogImageIdCache.set(pBasketId, tImageId);
                        return [2 /*return*/, (tImage)];
                }
            });
        });
    };
    /** @internal **/
    MainArticleElement.prototype._getCatalogImageId = function (pBasketId) {
        if (pBasketId == null)
            return (null);
        var tId = this.mCatalogImageIdCache.get(pBasketId);
        return (tId != null ? tId : null);
    };
    /** @internal */
    MainArticleElement.prototype._getCachedConfigData = function (pBasketId) {
        var tData = {};
        tData.articleData = this.mArticleDataCache.get(pBasketId);
        tData.articleDataOptions = this.mArticleDataOptionsCache.get(pBasketId);
        tData.oapInteractors = this.mOapInteractorsCache.get(pBasketId);
        tData.gfjUrl = this.basketId === pBasketId ? this.mGfjUrl : undefined;
        return (tData);
    };
    /** @internal */
    MainArticleElement.prototype._resetInstance = function () {
        this.mBasketId = null;
    };
    /** @internal */
    MainArticleElement.prototype._prepareInstanceRecoveryData = function () {
        return (this.ensureObx());
    };
    /** @internal */
    MainArticleElement.prototype._saveTransformToEaiws = function () {
        if (this.mEaiwsTransformUpToDate)
            return (Promise.resolve());
        if (!this.isInstanced() || this.mNode == null || this.mBasketId == null)
            return (rejectError(ERR_INVALID_STATE));
        this.mEaiwsTransformUpToDate = true;
        var tProps = new basket.ItemProperties();
        tProps.composableGeometry = new basket.ComposableGeometryProperties();
        var tGeomProps = tProps.composableGeometry;
        tGeomProps.enabled = true;
        //position
        tGeomProps.position = new basket.Vector3();
        tGeomProps.position.x = this.mNode.position.x;
        tGeomProps.position.y = this.mNode.position.y;
        tGeomProps.position.z = -this.mNode.position.z;
        //rotation
        var tAxisAngle = quaternionToAxisAngle(getRotationQuaternion(this.mNode));
        tGeomProps.rotation = new basket.Rotation();
        tGeomProps.rotation.axis = new basket.Vector3();
        tGeomProps.rotation.axis.x = tAxisAngle.x;
        tGeomProps.rotation.axis.y = tAxisAngle.y;
        tGeomProps.rotation.axis.z = -tAxisAngle.z;
        tGeomProps.rotation.angle = -tAxisAngle.w;
        //scale
        if (this.supportScaleEaiwsTransform()) {
            tGeomProps.scale = new basket.Vector3();
            tGeomProps.scale.x = this.mNode.scaling.x;
            tGeomProps.scale.y = this.mNode.scaling.y;
            tGeomProps.scale.z = this.mNode.scaling.z;
        }
        var tPromise = this.mMngr.session.basket.setItemProperties(this.mBasketId, tProps).catch(function (pError) {
            Log.error("Failed to save transformation", pError);
        });
        return (tPromise);
    };
    /** @internal */
    MainArticleElement.prototype._loadEaiwsTransform = function (pGeomProps) {
        if (pGeomProps == null || !pGeomProps.enabled)
            return;
        //position
        Helpers.Vector3CopyFromEaiwsVector(this.mNode.position, pGeomProps.position);
        //rotation
        this.mNode.rotationQuaternion = Helpers.QuaternionFromEaiwsRotation(pGeomProps.rotation);
        //scale
        if (this.supportScaleEaiwsTransform() && pGeomProps.scale != null) {
            this.mNode.scaling.x = pGeomProps.scale.x;
            this.mNode.scaling.y = pGeomProps.scale.y;
            this.mNode.scaling.z = pGeomProps.scale.z;
        }
        else {
            this.mNode.scaling.set(1, 1, 1);
        }
        this.mEaiwsTransformUpToDate = true;
        this.updateTransformation();
    };
    /** @internal */
    MainArticleElement.prototype._getOapInteractors = function (pArticle) {
        return __awaiter(this, void 0, void 0, function () {
            var tLockContext, tInteractors, tDpr;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (pArticle == null)
                            return [2 /*return*/, (null)];
                        if (!this.mMngr.session.isValid)
                            return [2 /*return*/, (null)];
                        return [4 /*yield*/, pArticle.ensureIsInstanced()];
                    case 1:
                        _a.sent();
                        if (pArticle.basketId == null)
                            throw newError(ERR_INVALID_STATE, "basketId is null");
                        return [4 /*yield*/, this.mOapInteractorsCacheLock.acquireLock()];
                    case 2:
                        tLockContext = _a.sent();
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, , 5, 6]);
                        tInteractors = this.mOapInteractorsCache.get(pArticle.basketId);
                        if (tInteractors != null)
                            return [2 /*return*/, (tInteractors)];
                        tDpr = window.devicePixelRatio > 1.5 ? 2 : 1;
                        return [4 /*yield*/, this.mMngr.session.basket.oapGetInteractors(pArticle.basketId, undefined, tDpr)];
                    case 4:
                        tInteractors = _a.sent();
                        this.mOapInteractorsCache.set(pArticle.basketId, tInteractors);
                        return [2 /*return*/, (tInteractors)];
                    case 5:
                        tLockContext.unlock();
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /** @internal */
    MainArticleElement._LoadArticleElement = function (pApp, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        var tIOArticle = pIOElement;
        var tIsPlaceholder = tIOArticle.placeholderGeometry != null ?
            tIOArticle.placeholderGeometry : false;
        var tModule = pApp.getModule(AppModule.MODULE_KEY);
        var tLoadedNode = MainArticleElement.LoadArticleElementNode(pApp, pContext, pIOElement);
        if (tLoadedNode == null || tLoadedNode.basketId == null || tModule == null)
            return (null);
        var tMainArticle = new MainArticleElement(tLoadedNode.basketId, tModule.articleManager, tLoadedNode.node);
        tMainArticle.setCatalogImageIdCache(tLoadedNode.catalogImageIds);
        if (tMainArticle.mNode == null)
            tIsPlaceholder = true;
        //register update geometry task
        if (tLoadedNode.geometryIncomplete && tModule.articleManager.session.isValid) {
            pContext.registerPendingTask(function () { return tMainArticle.updateGeometry(false); });
        }
        else if (tIsPlaceholder) {
            pContext.registerPendingTask(function () { return tMainArticle._initPlaceholderGeometry(null); });
        }
        return (tMainArticle);
    };
    /** @internal */
    MainArticleElement._UpdateOfmlArticle = function (pSession, pBasketId, pMigrate, pPriceDate) {
        var tOptions = new basket.UpdateBasketArticlesOptions();
        tOptions.migrate = pMigrate;
        tOptions.update = true;
        tOptions.wholeComposite = true;
        tOptions.priceDate = pPriceDate;
        var tPromise = pSession.basket.updateBasketArticles([pBasketId], null, tOptions).then(function (pResult) {
            var tState = "Unknown";
            if (pResult != null) {
                for (var _i = 0, pResult_1 = pResult; _i < pResult_1.length; _i++) {
                    var tRes = pResult_1[_i];
                    if (tRes.itemId === pBasketId) {
                        tState = tRes.updateState;
                        break;
                    }
                }
            }
            return (tState);
        });
        return (tPromise);
    };
    MainArticleElement.prototype._setGfjUrl = function (pUrl) {
        this.mGfjUrl = pUrl;
    };
    //// protected /////////////////////////////////////////////////////////////////////////////
    MainArticleElement.prototype.handleImmediateChanges = function () {
        _super.prototype.handleImmediateChanges.call(this);
        if (this.wasFlagChanged(ElementChangedFlags.Transformation)) {
            this.mEaiwsTransformUpToDate = false;
        }
        if (this.wasFlagChanged(ElementChangedFlags.Removed)) {
            //dispose dimensions
            this.showDimensions = false;
        }
    };
    MainArticleElement.prototype.copyDataFrom = function (pElement) {
        var _this = this;
        _super.prototype.copyDataFrom.call(this, pElement);
        if (pElement == null || !(pElement instanceof MainArticleElement))
            return;
        var tOther = pElement;
        //copy catalog image cache (with basket ids from other element)
        this.mCatalogImageIdCache = new Map(tOther.mCatalogImageIdCache);
        this.mHasPlaceholderGeometry = tOther.mHasPlaceholderGeometry;
        //set correct main article for cloned sub elements
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach(function (pSubElement) {
                pSubElement._setMainArticle(_this);
            });
        }
        //copy obx
        this.mEnsureObxPromise = tOther.ensureObx().then(function () {
            _this.mObxUrl = tOther.mObxUrl;
            _this.mEnsureObxPromise = null;
        });
    };
    MainArticleElement.prototype.setCatalogImageIdCache = function (pCache) {
        if (pCache != null)
            this.mCatalogImageIdCache = pCache;
    };
    MainArticleElement.LoadArticleElementNode = function (pApp, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        var tResult = {
            node: null,
            basketId: null,
            geometryIncomplete: false,
            catalogImageIds: new Map()
        };
        var tIOArticle = pIOElement;
        tResult.node = deserializeNode(pApp.model, pContext, tIOArticle.node, null, function (pIONode, pNode) {
            var tIONode = pIONode;
            //get article info
            if (tIONode.basketId != null) {
                //basket id
                NodeUserData.SetData(pNode, ArticleElement.NODEDATA_BASKETID, tIONode.basketId);
                //catalog image
                if (tIONode.catalogImage !== undefined) { //may be null
                    var tImgId = AppModule._GetCatalogImageByIndex(tIONode.catalogImage, pContext);
                    tResult.catalogImageIds.set(tIONode.basketId, tImgId);
                }
                //selectable
                var tSelectable = true;
                if (tIONode.selectable != null)
                    tSelectable = tIONode.selectable;
                NodeUserData.SetData(pNode, ArticleElement.NODEDATA_SELECTABLE, tSelectable);
            }
            if (pNode instanceof Mesh) {
                //is geometry missing?
                if (tIONode.geometry != null) {
                    if (pNode.geometry == null)
                        tResult.geometryIncomplete = true;
                    else if (pApp.model.geometries.isFallbackGeometry(pNode.geometry))
                        tResult.geometryIncomplete = true;
                }
                //is texture missing?
                if (tIONode.material != null) {
                    if (pNode.material == null)
                        tResult.geometryIncomplete = true;
                    else if (pNode.material instanceof PbrMaterial && pNode.material.hasFallbackTextures())
                        tResult.geometryIncomplete = true;
                }
            }
        });
        tResult.basketId = NodeUserData.GetData(tResult.node, ArticleElement.NODEDATA_BASKETID);
        if (tResult.node == null || tResult.basketId == null)
            return (null);
        return (tResult);
    };
    MainArticleElement.prototype.supportScaleEaiwsTransform = function () {
        return (false);
    };
    MainArticleElement.prototype.recoverInstance = function (pPasteObxIds) {
        var _this = this;
        if (this.mNode == null || pPasteObxIds == null || pPasteObxIds.length % 2 !== 0)
            return (false);
        //build map from old to new id
        var tIdMap = new Map();
        for (var i = 0; i < pPasteObxIds.length; i += 2)
            tIdMap.set(pPasteObxIds[i + 1], pPasteObxIds[i]);
        //fix ids in catalog image cache (copied in copyDataFrom)
        tIdMap.forEach(function (pNewId, pOldId) {
            var tImage = _this.mCatalogImageIdCache.get(pOldId);
            if (tImage !== undefined) //may be null
                _this.mCatalogImageIdCache.set(pNewId, tImage);
        });
        //recover subarticles
        if (this.mSubArticleCache == null)
            this.mSubArticleCache = new Map();
        else
            this.mSubArticleCache.clear();
        var tSubArticles = this.getSubArticles(false);
        for (var _i = 0, tSubArticles_5 = tSubArticles; _i < tSubArticles_5.length; _i++) {
            var tSubArticle = tSubArticles_5[_i];
            var tNewId_1 = tIdMap.get(tSubArticle._getRecoveryBasketId());
            if (tNewId_1 != null) {
                tSubArticle._setBasketId(tNewId_1);
                this.mSubArticleCache.set(tNewId_1, tSubArticle);
            }
        }
        //recover main article
        var tNewId = tIdMap.get(this.mRecoveryBasketId);
        if (tNewId != null) {
            //reset cached obx to ensure it contains the the correct basket ids the next time it is used
            this.mObxUrl = null;
            this._setBasketId(tNewId);
            this.mMngr._registerInstancedArticle(this);
            return (true);
        }
        return (false);
    };
    MainArticleElement.prototype.createOrUpdateSubArticleElements = function () {
        this.mHasSubArticles = false;
        this.mHasSelectableSubArticles = false;
        var tCachedSubArticles = new Map();
        this.createSubArticleElements(this.mNode, tCachedSubArticles);
        //trigger removed event for removed sub articles and dispose them
        if (this.mSubArticleCache != null) {
            this.mSubArticleCache.forEach(function (pElement, pId) {
                if (!tCachedSubArticles.has(pId)) {
                    pElement.notifyChange(ElementChangedFlags.Removed);
                    pElement.dispose();
                }
            });
        }
        this.mSubArticleCache = tCachedSubArticles;
    };
    MainArticleElement.prototype.createSubArticleElements = function (pParent, pNewCache) {
        if (pParent == null)
            return;
        var tChilds = pParent.getChildTransformNodes(true);
        for (var _i = 0, tChilds_1 = tChilds; _i < tChilds_1.length; _i++) {
            var tChild = tChilds_1[_i];
            var tBasketId = NodeUserData.GetData(tChild, ArticleElement.NODEDATA_BASKETID);
            if (tBasketId != null) {
                var tSubArticle = null;
                if (this.mSubArticleCache != null) //get from cache
                    tSubArticle = this.mSubArticleCache.get(tBasketId);
                if (tSubArticle != null) {
                    //store new selectable flag as it will be overriden from replaceNode
                    var tSelectable = NodeUserData.GetData(tChild, ArticleElement.NODEDATA_SELECTABLE);
                    tSubArticle.replaceNode(tChild, false, false, false);
                    //update selectable flag as it may have changed
                    NodeUserData.SetData(tSubArticle.node, ArticleElement.NODEDATA_SELECTABLE, tSelectable);
                }
                else {
                    tSubArticle = new SubArticleElement(this, tBasketId, this.mMngr, tChild);
                    //trigger added event
                    tSubArticle.notifyChange(ElementChangedFlags.Added);
                }
                this.mHasSubArticles = true;
                this.mHasSelectableSubArticles = this.mHasSelectableSubArticles || tSubArticle.canSelect();
                pNewCache.set(tBasketId, tSubArticle);
            }
            this.createSubArticleElements(tChild, pNewCache);
        }
    };
    MainArticleElement.prototype.ensureObx = function () {
        var _this = this;
        if (this.mEnsureObxPromise != null)
            return (this.mEnsureObxPromise); //reentrant, return existing promise
        if (!isNullOrEmpty(this.mObxUrl))
            return (Promise.resolve());
        if (this.mBasketId == null)
            return (rejectError(ERR_INVALID_STATE, "basketId is null"));
        this.mEnsureObxPromise = this.mMngr.session.basket.copy([this.mBasketId]).then(function (pUrl) {
            _this.mObxUrl = pUrl;
            _this.mEnsureObxPromise = null;
            return (Promise.resolve());
        }).catch(function (pError) {
            Log.error("Failed to get obx", pError);
            _this.mEnsureObxPromise = null;
        });
        return (this.mEnsureObxPromise);
    };
    MainArticleElement.prototype.getEaiwsBasketItems = function (pItemIds) {
        var tPromise;
        if (this.mMngr.session.isValid) {
            var tOptions = new basket.GetAllItemsOptions();
            tOptions.geometryIds = true;
            tOptions.setArticleIds = true;
            tPromise = this.mMngr.session.basket.getAllItems(pItemIds, tOptions);
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        return (tPromise);
    };
    MainArticleElement.prototype.getEaiwsItemProps = function (pItemIds) {
        var tPromise;
        if (this.mMngr.session.isValid) {
            tPromise = this.mMngr.session.basket.getItemProperties(pItemIds, this.mMngr._GetDefaultItemPropsOptions());
        }
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session is invalid");
        }
        return (tPromise);
    };
    MainArticleElement.prototype.getEaiwsItemCalculation = function (pItemIds) {
        var tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        var tPromise;
        if (this.mMngr.session.isValid)
            tPromise = this.mMngr.session.basket.getPriceCalculationSheets(pItemIds, tProc.name);
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session not valid");
        }
        return (tPromise);
    };
    MainArticleElement.prototype.getEaiwsCompositeCalculation = function (pItemId) {
        var tProc = this.mMngr.pricingProcedure;
        if (tProc == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        var tOptions = new basket.GetPriceCalculationSheetOptions();
        tOptions.wholeComposite = true;
        //ensure correct price for alternative articles
        tOptions.excludeInactiveItems = false;
        tOptions.includeInactiveItems = true;
        var tPromise;
        if (this.mMngr.session.isValid)
            tPromise = this.mMngr.session.basket.getPriceCalculationSheet([pItemId], tProc.name, tOptions);
        else {
            tPromise = rejectError(ERR_INVALID_STATE, "session not valid");
        }
        return (tPromise);
    };
    MainArticleElement.prototype.updateMainArticleVisibility = function () {
        var tNodes = this.getNodes(false);
        for (var _i = 0, tNodes_1 = tNodes; _i < tNodes_1.length; _i++) {
            var tNode = tNodes_1[_i];
            if (tNode instanceof AbstractMesh)
                tNode.isVisible = !this.mHideMainArticle;
        }
    };
    MainArticleElement.prototype.updateSubArticleVisibility = function () {
        var tSubArticles = this.getSubArticles(false);
        for (var _i = 0, tSubArticles_6 = tSubArticles; _i < tSubArticles_6.length; _i++) {
            var tArticle = tSubArticles_6[_i];
            var tIsVisible = this.mVisibleSubArticles == null ||
                contains(this.mVisibleSubArticles, tArticle.basketId);
            var tNodes = tArticle.getNodes(false);
            for (var _a = 0, tNodes_2 = tNodes; _a < tNodes_2.length; _a++) {
                var tNode = tNodes_2[_a];
                if (tNode instanceof AbstractMesh)
                    tNode.isVisible = tIsVisible;
            }
        }
    };
    MainArticleElement.prototype.updateBoxDimensions = function () {
        if (this.showDimensions) {
            this.showDimensions = false;
            this.showDimensions = true;
        }
    };
    MainArticleElement.CLASS_ID = "egr.wcf.cf:Article";
    return MainArticleElement;
}(ArticleElement));
export { MainArticleElement };
